import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { X } from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';

const EditTrackingModal = ({ open, onClose, sessionData, onUpdate }) => {
  const [formData, setFormData] = useState({
    session_notes: '',
    treatment_effectiveness: 50,
    improvement_action_notes: '',
    improvement_result_notes: '',
    therapy_list: [],
    report_image: '',
    recommended_next_steps: ''
  });
  const [loading, setLoading] = useState(false);

  const therapyOptions = [
    'Mat Therapy',
    'Jade Water Therapy',
    'CHM 123',
    'Mother Chair',
    'Acupressure',
    'Heat Therapy',
    'Massage',
    'Other'
  ];

  useEffect(() => {
    if (sessionData && open) {
      setFormData({
        session_notes: sessionData.session_notes || '',
        treatment_effectiveness: sessionData.treatment_effectiveness || 50,
        improvement_action_notes: sessionData.improvement_action_notes || '',
        improvement_result_notes: sessionData.improvement_result_notes || '',
        therapy_list: sessionData.therapy_list || [],
        report_image: sessionData.report_image || '',
        recommended_next_steps: sessionData.recommended_next_steps || ''
      });
    }
  }, [sessionData, open]);

  const handleTherapyToggle = (therapy) => {
    setFormData(prev => ({
      ...prev,
      therapy_list: prev.therapy_list.includes(therapy)
        ? prev.therapy_list.filter(t => t !== therapy)
        : [...prev.therapy_list, therapy]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.session_notes.trim()) {
      toast.error('Please enter session notes');
      return;
    }

    try {
      setLoading(true);
      await api.put(`/session-notes/${sessionData.note_id}`, formData);
      toast.success('Session notes updated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to update session notes:', error);
      toast.error(error.response?.data?.detail || 'Failed to update session notes');
    } finally {
      setLoading(false);
    }
  };

  if (!sessionData) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#2C4C3B]">
            Edit Session Notes
          </DialogTitle>
          <p className="text-sm text-[#1C1C1A]/60 mt-2">
            Customer: <span className="font-semibold">{sessionData.customer_name}</span> ({sessionData.customer_reg_no})
            <br />
            Date: {sessionData.session_date} | Bed: #{sessionData.bed_number}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Session Notes */}
          <div>
            <Label htmlFor="session_notes" className="text-[#2C4C3B] font-medium mb-2">
              Session Notes *
            </Label>
            <Textarea
              id="session_notes"
              value={formData.session_notes}
              onChange={(e) => setFormData({ ...formData, session_notes: e.target.value })}
              placeholder="Enter detailed session observations..."
              className="min-h-[100px] border-[#E5E5DF] focus:border-[#2C4C3B]"
              required
            />
          </div>

          {/* Treatment Effectiveness */}
          <div>
            <Label htmlFor="effectiveness" className="text-[#2C4C3B] font-medium mb-2">
              Treatment Effectiveness: {formData.treatment_effectiveness}%
            </Label>
            <input
              type="range"
              id="effectiveness"
              min="0"
              max="100"
              step="5"
              value={formData.treatment_effectiveness}
              onChange={(e) => setFormData({ ...formData, treatment_effectiveness: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2C4C3B]"
            />
            <div className="flex justify-between text-xs text-[#1C1C1A]/60 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Improvement Action */}
          <div>
            <Label htmlFor="improvement_action" className="text-[#2C4C3B] font-medium mb-2">
              Improvement Action
            </Label>
            <Textarea
              id="improvement_action"
              value={formData.improvement_action_notes}
              onChange={(e) => setFormData({ ...formData, improvement_action_notes: e.target.value })}
              placeholder="What actions were taken..."
              className="min-h-[80px] border-[#E5E5DF] focus:border-[#2C4C3B]"
            />
          </div>

          {/* Improvement Result */}
          <div>
            <Label htmlFor="improvement_result" className="text-[#2C4C3B] font-medium mb-2">
              Improvement Result %
            </Label>
            <Textarea
              id="improvement_result"
              value={formData.improvement_result_notes}
              onChange={(e) => setFormData({ ...formData, improvement_result_notes: e.target.value })}
              placeholder="Describe the results and improvements..."
              className="min-h-[80px] border-[#E5E5DF] focus:border-[#2C4C3B]"
            />
          </div>

          {/* Therapy List */}
          <div>
            <Label className="text-[#2C4C3B] font-medium mb-3 block">
              What Therapy Today?
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {therapyOptions.map((therapy) => (
                <label
                  key={therapy}
                  className="flex items-center gap-2 p-3 border border-[#E5E5DF] rounded-lg cursor-pointer hover:bg-[#E2E8E4] transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.therapy_list.includes(therapy)}
                    onChange={() => handleTherapyToggle(therapy)}
                    className="w-4 h-4 text-[#2C4C3B] rounded focus:ring-[#2C4C3B]"
                  />
                  <span className="text-sm text-[#1C1C1A]">{therapy}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Recommended Next Steps */}
          <div>
            <Label htmlFor="next_steps" className="text-[#2C4C3B] font-medium mb-2">
              Recommended Next Steps
            </Label>
            <Textarea
              id="next_steps"
              value={formData.recommended_next_steps}
              onChange={(e) => setFormData({ ...formData, recommended_next_steps: e.target.value })}
              placeholder="Recommendations for future sessions..."
              className="min-h-[80px] border-[#E5E5DF] focus:border-[#2C4C3B]"
            />
          </div>

          {/* Report Image URL (placeholder) */}
          <div>
            <Label htmlFor="report_image" className="text-[#2C4C3B] font-medium mb-2">
              Report Image URL (Optional)
            </Label>
            <Input
              id="report_image"
              type="text"
              value={formData.report_image}
              onChange={(e) => setFormData({ ...formData, report_image: e.target.value })}
              placeholder="https://..."
              className="border-[#E5E5DF] focus:border-[#2C4C3B]"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-[#E5E5DF]">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#E5E5DF]"
              disabled={loading}
            >
              <X size={18} className="mr-2" />
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#2C4C3B] hover:bg-[#1F3529]"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Update Session Notes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTrackingModal;
